// @flow

import type { Node } from "react";
import React from "react";

import { Slider, SliderFrame } from "@/components";
import JsonPageRenderer from "@/utils/JsonPageRenderer/JsonPageRenderer";
import type { ItemRenderer, PageItem } from "@/utils/JsonPageRenderer/types";

type Frame = {
    image: string,
};

interface SliderPageItem extends PageItem {
    props: {
        [string]: any,
        styleClass?: string,
        frames: {
            [index: number]: Frame,
        },
    };
}

/**
 * Workaround for Object.values() returning Array<mixed>
 * @param obj
 * @return {*}
 */
function values<T>(obj: { [index: number]: T }): T[] {
    function cast(value: any): T {
        return value;
    }

    return Object.values(obj).map(cast);
}

/**
 * Transforms a SliderPageItem into a Slider with SliderFrame nodes.
 */
class SliderRenderer implements ItemRenderer<SliderPageItem> {
    render(context: JsonPageRenderer, item: SliderPageItem): Node {
        function renderFrames(didLoad: (index: number) => void) {
            return values(item.props.frames).map((frame, frameIndex) => {
                const image = context.findImageSource(frame.image);
                if (typeof image === "string") {
                    return <SliderFrame key={frameIndex} className="slide" />;
                }

                const imageNoSourceSets = image
                    ? { original: image.original }
                    : undefined;

                return (
                    <SliderFrame
                        key={frameIndex}
                        className="slide"
                        didLoad={() => didLoad(frameIndex)}
                        backgroundImage={imageNoSourceSets}
                    />
                );
            });
        }

        return (
            <Slider styleClass={item.props.styleClass} key={item.id}>
                {didLoad => renderFrames(didLoad)}
            </Slider>
        );
    }

    match(item: PageItem): SliderPageItem | null {
        if (item.type === "slider") {
            return item;
        }
        return null;
    }
}

export default SliderRenderer;
